import React, { useEffect, useState, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Col,
  Row,
  Label,
  FormGroup,
  Input,
  Card,
  CardBody,
  Button,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import "../../assets/custom css/loadoverlay.scss";
import Alerts from "../../components/Alerts/alerts";
import ConfigurationService from "./configuration.service";
import AuthService from "../Authentication/auth.service";
import PermissionAccess from "../../PermissionAccess/Access.js";

/** Sandbox defaults from Meta "Step 1. Try it out" — replace with live values later */
const SANDBOX_DEFAULTS = {
  whatsapp_number: "+1 555 199 3242",
  whatsapp_phone_number_id: "1205498279323321",
  whatsapp_business_account_id: "1768044627723835",
  whatsapp_api_version: "v25.0",
  whatsapp_graph_base_url: "https://graph.facebook.com",
  whatsapp_otp_send_as: "template",
  whatsapp_otp_message_text:
    "*{{app_name}} Phone Verification*\n\n" +
    "Your verification code is: *{{otp}}*\n\n" +
    "Valid for {{expiry}}. Do not share this code with anyone.",
  whatsapp_otp_template_name: "jaspers_market_order_confirmation_v1",
  whatsapp_otp_template_language: "en_US",
  whatsapp_otp_template_mode: "sandbox",
  whatsapp_webhook_verify_token: "Vsara_WA_Verify_2026_Secret_Syslab",
};

const TabWhatsAppConfig = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [configs, setConfigs] = useState({});

  const [accessToken, setAccessToken] = useState("");
  const [phoneNumberId, setPhoneNumberId] = useState(
    SANDBOX_DEFAULTS.whatsapp_phone_number_id
  );
  const [businessAccountId, setBusinessAccountId] = useState(
    SANDBOX_DEFAULTS.whatsapp_business_account_id
  );
  const [whatsappNumber, setWhatsappNumber] = useState(
    SANDBOX_DEFAULTS.whatsapp_number
  );
  const [apiVersion, setApiVersion] = useState(
    SANDBOX_DEFAULTS.whatsapp_api_version
  );
  const [graphBaseUrl, setGraphBaseUrl] = useState(
    SANDBOX_DEFAULTS.whatsapp_graph_base_url
  );
  const [otpSendAs, setOtpSendAs] = useState(
    SANDBOX_DEFAULTS.whatsapp_otp_send_as
  );
  const [otpMessageText, setOtpMessageText] = useState(
    SANDBOX_DEFAULTS.whatsapp_otp_message_text
  );
  const [otpTemplateName, setOtpTemplateName] = useState(
    SANDBOX_DEFAULTS.whatsapp_otp_template_name
  );
  const [otpTemplateLanguage, setOtpTemplateLanguage] = useState(
    SANDBOX_DEFAULTS.whatsapp_otp_template_language
  );
  const [otpTemplateMode, setOtpTemplateMode] = useState(
    SANDBOX_DEFAULTS.whatsapp_otp_template_mode
  );
  const [webhookVerifyToken, setWebhookVerifyToken] = useState(
    SANDBOX_DEFAULTS.whatsapp_webhook_verify_token
  );
  const [otpExpirySeconds, setOtpExpirySeconds] = useState(300);
  const [otpMaxAttempts, setOtpMaxAttempts] = useState(5);
  const [otpMaxSends, setOtpMaxSends] = useState(10);
  const [otpResendCooldownSeconds, setOtpResendCooldownSeconds] = useState(600);

  const { authData } = PermissionAccess();

  useEffect(() => {
    i18n.changeLanguage(localStorage.getItem("language"));
    getSystemConfig();
  }, []);

  function getSystemConfig() {
    setLoading(true);
    ConfigurationService.getConfiguration(authData.sessionID).then(
      (response) => {
        setLoading(false);
        if (response.data.status) {
          const rec_data = response.data.data[0] || {};
          setConfigs(rec_data);

          setAccessToken(
            rec_data.whatsapp_access_token ||
              rec_data.facebook_whatsapp_token ||
              ""
          );
          setPhoneNumberId(
            rec_data.whatsapp_phone_number_id ||
              SANDBOX_DEFAULTS.whatsapp_phone_number_id
          );
          setBusinessAccountId(
            rec_data.whatsapp_business_account_id ||
              SANDBOX_DEFAULTS.whatsapp_business_account_id
          );
          setWhatsappNumber(
            rec_data.whatsapp_number || SANDBOX_DEFAULTS.whatsapp_number
          );
          setApiVersion(
            rec_data.whatsapp_api_version ||
              SANDBOX_DEFAULTS.whatsapp_api_version
          );
          setGraphBaseUrl(
            rec_data.whatsapp_graph_base_url ||
              SANDBOX_DEFAULTS.whatsapp_graph_base_url
          );
          setOtpSendAs(
            rec_data.whatsapp_otp_send_as ||
              SANDBOX_DEFAULTS.whatsapp_otp_send_as
          );
          setOtpMessageText(
            rec_data.whatsapp_otp_message_text ||
              SANDBOX_DEFAULTS.whatsapp_otp_message_text
          );
          setOtpTemplateName(
            rec_data.whatsapp_otp_template_name ||
              SANDBOX_DEFAULTS.whatsapp_otp_template_name
          );
          setOtpTemplateLanguage(
            rec_data.whatsapp_otp_template_language ||
              SANDBOX_DEFAULTS.whatsapp_otp_template_language
          );
          setOtpTemplateMode(
            rec_data.whatsapp_otp_template_mode ||
              SANDBOX_DEFAULTS.whatsapp_otp_template_mode
          );
          setWebhookVerifyToken(
            rec_data.whatsapp_webhook_verify_token ||
              SANDBOX_DEFAULTS.whatsapp_webhook_verify_token
          );
          setOtpExpirySeconds(Number(rec_data.otp_expiry_seconds) || 300);
          setOtpMaxAttempts(Number(rec_data.otp_max_attempts) || 5);
          setOtpMaxSends(Number(rec_data.otp_max_sends) || 10);
          setOtpResendCooldownSeconds(
            Number(rec_data.otp_resend_cooldown_seconds) || 600
          );
        } else if (response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
        } else {
          Alerts.swalErrorAlert(
            t("Unable to retreive configurations at the moment.")
          );
        }
      },
      () => {
        setLoading(false);
        Alerts.swalErrorAlert(
          t("Unable to retreive configurations at the moment.")
        );
      }
    );
  }

  const onSubmit = (e) => {
    e.preventDefault();

    if (!String(accessToken || "").trim()) {
      Alerts.swalErrorAlert(t("Access Token is required."));
      return;
    }
    if (!String(phoneNumberId || "").trim()) {
      Alerts.swalErrorAlert(t("Phone Number ID is required."));
      return;
    }

    const payloadData = {
      functionName: "SetConfigurations",
      postData: {
        ...configs,
        // Meta WhatsApp Cloud API (replaces 360Messenger)
        whatsapp_access_token: String(accessToken || "").trim(),
        whatsapp_phone_number_id: String(phoneNumberId || "").trim(),
        whatsapp_business_account_id: String(businessAccountId || "").trim(),
        whatsapp_number: String(whatsappNumber || "").trim(),
        whatsapp_api_version:
          String(apiVersion || "").trim() ||
          SANDBOX_DEFAULTS.whatsapp_api_version,
        whatsapp_graph_base_url:
          String(graphBaseUrl || "").trim() ||
          SANDBOX_DEFAULTS.whatsapp_graph_base_url,
        whatsapp_otp_send_as:
          String(otpSendAs || "").trim() ||
          SANDBOX_DEFAULTS.whatsapp_otp_send_as,
        whatsapp_otp_message_text:
          String(otpMessageText || "").trim() ||
          SANDBOX_DEFAULTS.whatsapp_otp_message_text,
        whatsapp_otp_template_name:
          String(otpTemplateName || "").trim() ||
          SANDBOX_DEFAULTS.whatsapp_otp_template_name,
        whatsapp_otp_template_language:
          String(otpTemplateLanguage || "").trim() ||
          SANDBOX_DEFAULTS.whatsapp_otp_template_language,
        whatsapp_otp_template_mode:
          String(otpTemplateMode || "").trim() ||
          SANDBOX_DEFAULTS.whatsapp_otp_template_mode,
        whatsapp_webhook_verify_token:
          String(webhookVerifyToken || "").trim() ||
          SANDBOX_DEFAULTS.whatsapp_webhook_verify_token,
        otp_expiry_seconds: Number(otpExpirySeconds) || 300,
        otp_max_attempts: Number(otpMaxAttempts) || 5,
        otp_max_sends: Number(otpMaxSends) || 10,
        otp_resend_cooldown_seconds: Number(otpResendCooldownSeconds) || 600,
        // Clear legacy 360Messenger fields
        messenger360_api_key: "",
        messenger360_api_url: "",
        messenger360_whatsapp_number: "",
        whatsapp_api_key: "",
      },
    };

    setLoading(true);
    ConfigurationService.setConfiguration(authData.sessionID, payloadData).then(
      (response) => {
        setLoading(false);
        if (response.data.status) {
          Alerts.swalSuccessAlert(
            t("WhatsApp & OTP configuration updated successfully.")
          );
          getSystemConfig();
        } else {
          Alerts.swalErrorAlert(
            response.data.message || t("Failed to update configurations.")
          );
        }
      },
      () => {
        setLoading(false);
        Alerts.swalErrorAlert(t("Network error. Please try again."));
      }
    );
  };

  return (
    <Fragment>
      <div className="container-fluid">
        <Row>
          <Col md={12}>
            <Card
              className="main-card mb-3 border-0 shadow-sm"
              style={{ borderRadius: "15px" }}
            >
              <CardBody className="p-4">
                <div className="mb-4">
                  <h4 className="fw-bold text-dark mb-1">
                    {t("WhatsApp OTP — Meta Cloud API")}
                  </h4>
                  <p className="text-muted mb-0">
                    {t(
                      "Same place as before: save Meta credentials here. Sandbox values are prefilled — paste Access Token and Save. Later replace with live keys/template."
                    )}
                  </p>
                </div>

                <LoadingOverlay
                  tag="div"
                  active={loading}
                  styles={{
                    overlay: (base) => ({ ...base, borderRadius: "15px" }),
                  }}
                  spinner={<DNALoader />}
                >
                  <form onSubmit={onSubmit}>
                    <h5 className="fw-bold mb-3">{t("Meta Credentials")}</h5>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="whatsapp_number">
                            {t("WhatsApp Number")}
                          </Label>
                          <Input
                            id="whatsapp_number"
                            type="text"
                            value={whatsappNumber}
                            onChange={(e) => setWhatsappNumber(e.target.value)}
                            placeholder="+1 555 199 3242"
                          />
                          <small className="text-muted">
                            {t(
                              "Display / test number from Meta dashboard (optional)."
                            )}
                          </small>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="whatsapp_phone_number_id">
                            {t("Phone Number ID")} *
                          </Label>
                          <Input
                            id="whatsapp_phone_number_id"
                            type="text"
                            value={phoneNumberId}
                            onChange={(e) => setPhoneNumberId(e.target.value)}
                            placeholder="1205498279323321"
                            required
                          />
                          <small className="text-muted">
                            {t(
                              "Used in API URL: graph.facebook.com/{version}/{Phone-Number-ID}/messages"
                            )}
                          </small>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="whatsapp_business_account_id">
                            {t("WhatsApp Business Account ID")}
                          </Label>
                          <Input
                            id="whatsapp_business_account_id"
                            type="text"
                            value={businessAccountId}
                            onChange={(e) =>
                              setBusinessAccountId(e.target.value)
                            }
                            placeholder="1768044627723835"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="whatsapp_api_version">
                            {t("Graph API Version")}
                          </Label>
                          <Input
                            id="whatsapp_api_version"
                            type="text"
                            value={apiVersion}
                            onChange={(e) => setApiVersion(e.target.value)}
                            placeholder="v25.0"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={12}>
                        <FormGroup>
                          <Label for="whatsapp_access_token">
                            {t("Access Token")} *
                          </Label>
                          <InputGroup>
                            <Input
                              id="whatsapp_access_token"
                              type={showToken ? "text" : "password"}
                              autoComplete="new-password"
                              value={accessToken}
                              onChange={(e) => setAccessToken(e.target.value)}
                              placeholder={t(
                                "Paste Meta temporary or permanent token"
                              )}
                              required
                            />
                            <InputGroupText
                              tag="button"
                              type="button"
                              style={{ cursor: "pointer" }}
                              onClick={() => setShowToken((v) => !v)}
                            >
                              {showToken ? t("Hide") : t("Show")}
                            </InputGroupText>
                          </InputGroup>
                          <small className="text-muted">
                            {t(
                              "Sandbox temp token expires (~24h). For production use a System User permanent token."
                            )}
                          </small>
                        </FormGroup>
                      </Col>
                      <Col md={12}>
                        <FormGroup>
                          <Label for="whatsapp_graph_base_url">
                            {t("Graph API Base URL")}
                          </Label>
                          <Input
                            id="whatsapp_graph_base_url"
                            type="text"
                            value={graphBaseUrl}
                            onChange={(e) => setGraphBaseUrl(e.target.value)}
                            placeholder="https://graph.facebook.com"
                          />
                          <small className="text-muted">
                            {t(
                              "Usually https://graph.facebook.com (same role as old 360 API Base URL)."
                            )}
                          </small>
                        </FormGroup>
                      </Col>
                    </Row>

                    <hr className="my-4" />
                    <h5 className="fw-bold mb-1">{t("OTP Message")}</h5>
                    <p className="text-muted mb-3">
                      {t(
                        "Sandbox sample template text is FIXED by Meta (order confirmation). Custom OTP wording needs your own approved Auth/OTP template — or Text mode (only works inside Meta 24h window)."
                      )}
                    </p>
                    <Row>
                      <Col md={12}>
                        <FormGroup>
                          <Label for="whatsapp_otp_send_as">
                            {t("Send As")}
                          </Label>
                          <Input
                            id="whatsapp_otp_send_as"
                            type="select"
                            value={otpSendAs}
                            onChange={(e) => setOtpSendAs(e.target.value)}
                          >
                            <option value="template">
                              {t("Template — recommended (sandbox / OTP)")}
                            </option>
                            <option value="text">
                              {t("Text — custom wording (24h window only)")}
                            </option>
                          </Input>
                          <small className="text-muted d-block mt-1">
                            {otpSendAs === "template"
                              ? t(
                                  "Uses Meta template. Sandbox jaspers_… wording cannot be edited — only name / OTP / date params. For custom text create an Auth template in Meta."
                                )
                              : t(
                                  "Uses Configuration message text below. Meta may block this outside 24h chat window — if so, send auto-falls back to template."
                                )}
                          </small>
                        </FormGroup>
                      </Col>
                      {otpSendAs === "text" && (
                        <Col md={12}>
                          <FormGroup>
                            <Label for="whatsapp_otp_message_text">
                              {t("OTP Message Text")}
                            </Label>
                            <Input
                              id="whatsapp_otp_message_text"
                              type="textarea"
                              rows={6}
                              value={otpMessageText}
                              onChange={(e) => setOtpMessageText(e.target.value)}
                              placeholder={
                                SANDBOX_DEFAULTS.whatsapp_otp_message_text
                              }
                            />
                            <small className="text-muted">
                              {t(
                                "Placeholders: {{otp}} {{expiry}} {{app_name}} — these are replaced when sending."
                              )}
                            </small>
                          </FormGroup>
                        </Col>
                      )}
                      <Col md={4}>
                        <FormGroup>
                          <Label for="whatsapp_otp_template_name">
                            {t("Template Name")}
                          </Label>
                          <Input
                            id="whatsapp_otp_template_name"
                            type="text"
                            value={otpTemplateName}
                            onChange={(e) => setOtpTemplateName(e.target.value)}
                            placeholder={
                              SANDBOX_DEFAULTS.whatsapp_otp_template_name
                            }
                            disabled={otpSendAs !== "template"}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label for="whatsapp_otp_template_language">
                            {t("Template Language")}
                          </Label>
                          <Input
                            id="whatsapp_otp_template_language"
                            type="text"
                            value={otpTemplateLanguage}
                            onChange={(e) =>
                              setOtpTemplateLanguage(e.target.value)
                            }
                            placeholder="en_US"
                            disabled={otpSendAs !== "template"}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label for="whatsapp_otp_template_mode">
                            {t("Param Mode")}
                          </Label>
                          <Input
                            id="whatsapp_otp_template_mode"
                            type="select"
                            value={otpTemplateMode}
                            onChange={(e) => setOtpTemplateMode(e.target.value)}
                            disabled={otpSendAs !== "template"}
                          >
                            <option value="sandbox">
                              {t("Sandbox (name + OTP + date)")}
                            </option>
                            <option value="otp">
                              {t("OTP only (live auth template)")}
                            </option>
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>

                    <hr className="my-4" />
                    <h5 className="fw-bold mb-1">{t("Meta Webhook (Production)")}</h5>
                    <p className="text-muted mb-3">
                      {t(
                        "Paste this Callback URL + Verify token in Meta → Step 2 → Configure Webhooks. Server must be publicly reachable on HTTPS."
                      )}
                    </p>
                    <Row>
                      <Col md={12}>
                        <FormGroup>
                          <Label>{t("Callback URL")}</Label>
                          <Input
                            type="text"
                            readOnly
                            value={`${String(
                              process.env.REACT_APP_APIURL || ""
                            ).replace(/\/$/, "")}/WhatsAppWebhook`}
                            onFocus={(e) => e.target.select()}
                          />
                          <small className="text-muted">
                            {t(
                              "Recommended (live): https://api.hatsb.com/api/WhatsAppWebhook — Staging: https://altitudeprojects.net:3012/api/WhatsAppWebhook"
                            )}
                          </small>
                        </FormGroup>
                      </Col>
                      <Col md={12}>
                        <FormGroup>
                          <Label for="whatsapp_webhook_verify_token">
                            {t("Verify Token")}
                          </Label>
                          <Input
                            id="whatsapp_webhook_verify_token"
                            type="text"
                            value={webhookVerifyToken}
                            onChange={(e) =>
                              setWebhookVerifyToken(e.target.value)
                            }
                            placeholder="vsara_whatsapp_verify_token"
                          />
                          <small className="text-muted">
                            {t(
                              "Same value must be saved here AND entered in Meta Verify token field."
                            )}
                          </small>
                        </FormGroup>
                      </Col>
                    </Row>

                    <hr className="my-4" />
                    <h5 className="fw-bold mb-1">{t("OTP Policy")}</h5>
                    <p className="text-muted mb-3">
                      {t(
                        "Applies to both Email OTP and WhatsApp OTP (Doctor, Patient, Merchant)."
                      )}
                    </p>

                    <Row>
                      <Col md={3}>
                        <FormGroup>
                          <Label for="otp_expiry_seconds">
                            {t("OTP Expiry (seconds)")}
                          </Label>
                          <Input
                            id="otp_expiry_seconds"
                            type="number"
                            min={30}
                            value={otpExpirySeconds}
                            onChange={(e) =>
                              setOtpExpirySeconds(e.target.value)
                            }
                          />
                          <small className="text-muted">
                            {t("e.g. 300 = OTP dies after 5 min")}
                          </small>
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label for="otp_max_attempts">
                            {t("Max Verify Tries")}
                          </Label>
                          <Input
                            id="otp_max_attempts"
                            type="number"
                            min={1}
                            value={otpMaxAttempts}
                            onChange={(e) => setOtpMaxAttempts(e.target.value)}
                          />
                          <small className="text-muted">
                            {t("e.g. 5 wrong codes → OTP invalid")}
                          </small>
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label for="otp_max_sends">
                            {t("Max OTP Sends")}
                          </Label>
                          <Input
                            id="otp_max_sends"
                            type="number"
                            min={1}
                            value={otpMaxSends}
                            onChange={(e) => setOtpMaxSends(e.target.value)}
                          />
                          <small className="text-muted">
                            {t("e.g. 10 free sends, then cooldown")}
                          </small>
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label for="otp_resend_cooldown_seconds">
                            {t("Cooldown After Max Sends (sec)")}
                          </Label>
                          <Input
                            id="otp_resend_cooldown_seconds"
                            type="number"
                            min={0}
                            value={otpResendCooldownSeconds}
                            onChange={(e) =>
                              setOtpResendCooldownSeconds(e.target.value)
                            }
                          />
                          <small className="text-muted">
                            {t("e.g. 600 = wait 10 min on 11th request")}
                          </small>
                        </FormGroup>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-end mt-3">
                      <Button color="success" type="submit">
                        {t("Save")}
                      </Button>
                    </div>
                  </form>
                </LoadingOverlay>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </Fragment>
  );
};

export default TabWhatsAppConfig;
